import { ethers } from 'ethers';
import { getFromStorage } from '../utils/storage';

// AVO Network configurations
const AVO_NETWORKS = {
  devnet: {
    name: 'AVO Devnet',
    chainId: 1337,
    rpcUrl: 'http://localhost:9545',
    symbol: 'AVO',
    decimals: 18,
    blockExplorer: null
  },
  testnet: {
    name: 'AVO Testnet',
    chainId: 1338,
    rpcUrl: 'https://testnet-rpc.avo.network', // Placeholder for future
    symbol: 'AVO',
    decimals: 18,
    blockExplorer: 'https://testnet-explorer.avo.network'
  },
  mainnet: {
    name: 'AVO Mainnet',
    chainId: 1339,
    rpcUrl: 'https://mainnet-rpc.avo.network', // Placeholder for future
    symbol: 'AVO',
    decimals: 18,
    blockExplorer: 'https://explorer.avo.network'
  }
};

/**
 * Gets AVO RPC provider for specified network
 * @param {string} [network='devnet'] - Network name (devnet, testnet, mainnet)
 * @returns {ethers.providers.JsonRpcProvider} - Network provider
 */
export const getAvoProvider = (network = 'devnet') => {
  try {
    const networkConfig = AVO_NETWORKS[network];
    if (!networkConfig) {
      console.warn(`Unknown AVO network: ${network}, defaulting to devnet`);
      return new ethers.providers.JsonRpcProvider(AVO_NETWORKS.devnet.rpcUrl);
    }
    
    console.log(`🚀 Connecting to AVO ${network}:`, networkConfig.rpcUrl);
    return new ethers.providers.JsonRpcProvider(networkConfig.rpcUrl);
  } catch (err) {
    console.error('Error creating AVO provider:', { err, network });
    console.warn('Falling back to devnet');
    return new ethers.providers.JsonRpcProvider(AVO_NETWORKS.devnet.rpcUrl);
  }
};

/**
 * Gets AVO balance for an address
 * @param {string} address - AVO address
 * @param {string} [network='devnet'] - Network name
 * @returns {Promise<string>} - Balance in AVO tokens
 */
export const getAvoBalance = async (address, network = null) => {
  try {
    if (!ethers.utils.isAddress(address)) {
      throw new Error('Invalid AVO address');
    }
    
    const effectiveNetwork = network || (await getFromStorage('currentAvoNetwork')) || 'devnet';
    const provider = getAvoProvider(effectiveNetwork);
    
    // For AVO devnet, use direct RPC call to get balance
    if (effectiveNetwork === 'devnet') {
      try {
        const response = await fetch('http://localhost:9545', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            jsonrpc: '2.0',
            method: 'eth_getBalance',
            params: [address, 'latest'],
            id: 1
          })
        });
        
        const data = await response.json();
        if (data.error) {
          throw new Error(data.error.message);
        }
        
        const balanceWei = ethers.BigNumber.from(data.result);
        return ethers.utils.formatEther(balanceWei);
      } catch (rpcError) {
        console.warn('Direct RPC failed, falling back to provider:', rpcError);
      }
    }
    
    const balance = await provider.getBalance(address);
    return ethers.utils.formatEther(balance);
  } catch (err) {
    console.error('Error getting AVO balance:', { err, address, network });
    throw new Error(`Error getting AVO balance: ${err.message}`);
  }
};

/**
 * Sends AVO transaction
 * @param {ethers.Wallet} wallet - Wallet with private key
 * @param {string} to - Recipient address
 * @param {string|number} amount - Amount in AVO
 * @param {string} [network='devnet'] - Network name
 * @param {Object} [txOptions={}] - Transaction options
 * @returns {Promise<Object>} - Transaction details
 */
export const sendAvoTransaction = async (wallet, to, amount, network = null, txOptions = {}) => {
  try {
    if (!ethers.utils.isAddress(to)) {
      throw new Error('Invalid recipient address');
    }
    if (!wallet || typeof wallet.sendTransaction !== 'function') {
      throw new Error('Invalid wallet');
    }
    if (parseFloat(amount) <= 0) {
      throw new Error('Amount must be greater than 0');
    }
    
    // Validate active session
    const sessionActive = await getFromStorage('sessionActive');
    if (sessionActive !== 'true') {
      throw new Error('Session not active, please login');
    }

    const effectiveNetwork = network || (await getFromStorage('currentAvoNetwork')) || 'devnet';
    const provider = getAvoProvider(effectiveNetwork);
    const walletWithProvider = wallet.connect(provider);
    const amountInWei = ethers.utils.parseEther(amount.toString());

    // For AVO devnet, use direct RPC call
    if (effectiveNetwork === 'devnet') {
      try {
        // Get gas price
        const gasPriceResponse = await fetch('http://localhost:9545', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            jsonrpc: '2.0',
            method: 'eth_gasPrice',
            params: [],
            id: 1
          })
        });
        
        const gasPriceData = await gasPriceResponse.json();
        const gasPrice = gasPriceData.result || '0x3b9aca00'; // 1 Gwei fallback
        
        // Build transaction
        const tx = {
          to,
          value: amountInWei,
          gasLimit: ethers.BigNumber.from(txOptions.gasLimit || '21000'),
          gasPrice: ethers.BigNumber.from(gasPrice),
          type: 0 // Legacy transaction for AVO devnet
        };

        console.log('🚀 Sending AVO transaction:', { network: effectiveNetwork, tx });
        const transactionResponse = await walletWithProvider.sendTransaction(tx);
        console.log('✅ AVO transaction sent:', transactionResponse.hash);

        const receipt = await transactionResponse.wait();
        console.log('📋 AVO transaction receipt:', receipt);

        return {
          hash: transactionResponse.hash,
          from: receipt.from,
          to: receipt.to,
          value: amount,
          blockNumber: receipt.blockNumber,
          network: effectiveNetwork,
          currency: 'AVO'
        };
      } catch (rpcError) {
        console.warn('Direct AVO RPC failed, falling back to provider:', rpcError);
      }
    }

    // Fallback to standard provider method
    const tx = {
      to,
      value: amountInWei,
      gasLimit: ethers.BigNumber.from(txOptions.gasLimit || '21000'),
      gasPrice: ethers.BigNumber.from(txOptions.gasPrice || '1000000000'), // 1 Gwei
    };

    const transactionResponse = await walletWithProvider.sendTransaction(tx);
    const receipt = await transactionResponse.wait();

    return {
      hash: transactionResponse.hash,
      from: receipt.from,
      to: receipt.to,
      value: amount,
      blockNumber: receipt.blockNumber,
      network: effectiveNetwork,
      currency: 'AVO'
    };
  } catch (err) {
    console.error('Error sending AVO transaction:', { err, network, to, amount });
    if (err.message.includes('Session not active')) {
      throw err;
    }
    if (err.code === 'INSUFFICIENT_FUNDS') {
      throw new Error('Insufficient AVO balance for transaction and gas');
    }
    throw new Error(`Error sending AVO transaction: ${err.message}`);
  }
};

/**
 * Estimates AVO transaction gas cost
 * @param {string} fromAddress - Sender address
 * @param {string} toAddress - Recipient address
 * @param {string} [network='devnet'] - Network name
 * @returns {Promise<Object>} - Gas cost options (low, medium, high)
 */
export const estimateAvoGasCost = async (fromAddress, toAddress, network = null) => {
  try {
    if (!ethers.utils.isAddress(fromAddress) || !ethers.utils.isAddress(toAddress)) {
      throw new Error('Invalid addresses');
    }

    const effectiveNetwork = network || (await getFromStorage('currentAvoNetwork')) || 'devnet';
    
    // For AVO devnet, use simple gas calculation
    if (effectiveNetwork === 'devnet') {
      const gasLimit = '21000'; // Standard transfer
      const gasPrice = '1000000000'; // 1 Gwei
      const estimatedCost = ethers.BigNumber.from(gasLimit).mul(gasPrice).toString();
      
      return {
        low: {
          gasPrice,
          gasLimit,
          estimatedCost,
          estimatedCostFormatted: ethers.utils.formatEther(estimatedCost) + ' AVO'
        },
        medium: {
          gasPrice: ethers.BigNumber.from(gasPrice).mul(2).toString(),
          gasLimit,
          estimatedCost: ethers.BigNumber.from(gasLimit).mul(gasPrice).mul(2).toString(),
          estimatedCostFormatted: ethers.utils.formatEther(ethers.BigNumber.from(gasLimit).mul(gasPrice).mul(2)) + ' AVO'
        },
        high: {
          gasPrice: ethers.BigNumber.from(gasPrice).mul(3).toString(),
          gasLimit,
          estimatedCost: ethers.BigNumber.from(gasLimit).mul(gasPrice).mul(3).toString(),
          estimatedCostFormatted: ethers.utils.formatEther(ethers.BigNumber.from(gasLimit).mul(gasPrice).mul(3)) + ' AVO'
        }
      };
    }

    // For other networks, use provider estimation
    const provider = getAvoProvider(effectiveNetwork);
    const gasLimit = '21000';
    const gasPrice = '1000000000';
    const estimatedCost = ethers.BigNumber.from(gasLimit).mul(gasPrice).toString();

    return {
      low: { gasPrice, gasLimit, estimatedCost },
      medium: { gasPrice, gasLimit, estimatedCost },
      high: { gasPrice, gasLimit, estimatedCost }
    };
  } catch (error) {
    console.error('Error estimating AVO gas cost:', { error, network, fromAddress, toAddress });
    
    const defaultGasLimit = '21000';
    const defaultGasPrice = '1000000000';
    const defaultCost = ethers.BigNumber.from(defaultGasLimit).mul(defaultGasPrice).toString();

    return {
      low: { gasPrice: defaultGasPrice, gasLimit: defaultGasLimit, estimatedCost: defaultCost },
      medium: { gasPrice: defaultGasPrice, gasLimit: defaultGasLimit, estimatedCost: defaultCost },
      high: { gasPrice: defaultGasPrice, gasLimit: defaultGasLimit, estimatedCost: defaultCost }
    };
  }
};

/**
 * Gets current AVO network configuration
 * @param {string} network - Network name
 * @returns {Object} - Network configuration
 */
export const getAvoNetworkConfig = (network = 'devnet') => {
  return AVO_NETWORKS[network] || AVO_NETWORKS.devnet;
};

/**
 * Gets available AVO networks
 * @returns {Object} - Available networks
 */
export const getAvoNetworks = () => {
  return AVO_NETWORKS;
};

/**
 * Gets recent AVO transactions for an address
 * Note: This is a placeholder - implement based on AVO node's transaction history API
 * @param {string} address - Address to get transactions for
 * @param {string} [network='devnet'] - Network name
 * @returns {Promise<Array>} - Recent transactions
 */
export const getAvoTransactionHistory = async (address, network = null) => {
  try {
    const effectiveNetwork = network || (await getFromStorage('currentAvoNetwork')) || 'devnet';
    
    // For now, return empty array - implement when AVO node has transaction history API
    console.log(`📋 Getting AVO transaction history for ${address} on ${effectiveNetwork}`);
    
    // TODO: Implement when AVO node supports transaction history queries
    return [];
  } catch (err) {
    console.error('Error getting AVO transaction history:', { err, address, network });
    return [];
  }
};

/**
 * Gets current AVO network status
 * @param {string} [network='devnet'] - Network name  
 * @returns {Promise<Object>} - Network status
 */
export const getAvoNetworkStatus = async (network = null) => {
  try {
    const effectiveNetwork = network || (await getFromStorage('currentAvoNetwork')) || 'devnet';
    
    if (effectiveNetwork === 'devnet') {
      // Get network info from AVO node
      const response = await fetch('http://localhost:9545', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jsonrpc: '2.0',
          method: 'eth_chainId',
          params: [],
          id: 1
        })
      });
      
      const data = await response.json();
      if (data.error) {
        throw new Error(data.error.message);
      }
      
      return {
        network: effectiveNetwork,
        chainId: parseInt(data.result, 16),
        connected: true,
        rpcUrl: AVO_NETWORKS[effectiveNetwork].rpcUrl
      };
    }
    
    return {
      network: effectiveNetwork,
      chainId: AVO_NETWORKS[effectiveNetwork].chainId,
      connected: false,
      rpcUrl: AVO_NETWORKS[effectiveNetwork].rpcUrl
    };
  } catch (err) {
    console.error('Error getting AVO network status:', { err, network });
    return {
      network: network || 'devnet',
      chainId: null,
      connected: false,
      error: err.message
    };
  }
};

/**
 * Get AVO price in USD (mock data)
 */
export const getAvoPrice = async () => {
  try {
    // Mock price data - in real implementation, you'd fetch from price API
    // You could connect this to a price oracle or external API
    const basePrice = 125.50;
    const mockPriceChange = (Math.random() - 0.5) * 10; // -5% to +5%
    
    return {
      currentPrice: basePrice,
      priceChange24h: mockPriceChange,
      marketCap: basePrice * 1000000, // Mock market cap
      volume24h: basePrice * 50000    // Mock volume
    };
  } catch (error) {
    console.error('Error getting AVO price:', error);
    return {
      currentPrice: 0,
      priceChange24h: 0,
      marketCap: 0,
      volume24h: 0
    };
  }
};

/**
 * Get token prices for AVO ecosystem tokens
 */
export const getAvoTokenPrices = async () => {
  try {
    const avoPrice = await getAvoPrice();
    
    return {
      'AVO': avoPrice.currentPrice,
      'vAVO': avoPrice.currentPrice * 0.36, // Governance token at 36% of AVO price
      'sAVO': avoPrice.currentPrice // Staking token pegged to AVO
    };
  } catch (error) {
    console.error('Error getting AVO token prices:', error);
    return {
      'AVO': 0,
      'vAVO': 0, 
      'sAVO': 0
    };
  }
};

/**
 * Get recent AVO transactions for an address
 */
export const getAvoRecentTransactions = async (address) => {
  try {
    // Try to get real transaction history from AVO network
    // For now, we'll use the existing transaction history function
    const transactions = await getAvoTransactionHistory(address);
    
    // Convert to the format expected by dashboard
    return transactions.map(tx => ({
      hash: tx.hash,
      type: tx.from.toLowerCase() === address.toLowerCase() ? 'Sent AVO' : 'Received AVO',
      amount: ethers.utils.formatEther(tx.value || '0'),
      from: tx.from,
      to: tx.to,
      timestamp: tx.timestamp,
      status: tx.status || 'Success',
      gasUsed: tx.gasUsed || '21000',
      gasPrice: ethers.utils.formatUnits(tx.gasPrice || '0', 'gwei')
    }));
  } catch (error) {
    console.warn('Could not get real transactions, using empty array:', error.message);
    // Return empty array if we can't get real transactions
    return [];
  }
};

/**
 * Get AVO token balances (for AVO-based tokens)
 */
export const getAvoTokenBalances = async (address) => {
  try {
    const provider = getAvoProvider();
    
    // Try to get real token balances from AVO network
    // For now, return empty array until token contracts are deployed
    const tokenContracts = [
      {
        name: 'AVO Governance Token',
        symbol: 'vAVO',
        address: '0x1234567890123456789012345678901234567890', // Placeholder
        decimals: 18
      },
      {
        name: 'AVO Staking Rewards',
        symbol: 'sAVO', 
        address: '0x2345678901234567890123456789012345678901', // Placeholder
        decimals: 18
      }
    ];

    const tokens = [];
    
    // For each token contract, try to get balance
    for (const tokenConfig of tokenContracts) {
      try {
        // In real implementation, you would query the token contract
        // const contract = new ethers.Contract(tokenConfig.address, ERC20_ABI, provider);
        // const balance = await contract.balanceOf(address);
        
        // For now, return 0 balance until contracts are deployed
        const balance = '0';
        
        if (parseFloat(balance) > 0) {
          const prices = await getAvoTokenPrices();
          const price = prices[tokenConfig.symbol] || 0;
          const balanceNum = parseFloat(ethers.utils.formatUnits(balance, tokenConfig.decimals));
          
          tokens.push({
            name: tokenConfig.name,
            symbol: tokenConfig.symbol,
            balance: balanceNum.toFixed(4),
            price: price,
            value: balanceNum * price,
            address: tokenConfig.address,
            decimals: tokenConfig.decimals
          });
        }
      } catch (err) {
        console.warn(`Could not get balance for token ${tokenConfig.symbol}:`, err.message);
      }
    }
    
    return tokens;
  } catch (error) {
    console.error('Error getting AVO token balances:', error);
    return [];
  }
};

/**
 * Get current AVO network information
 */
export const getCurrentAvoNetwork = async () => {
  try {
    const provider = getAvoProvider();
    
    // Try to get network info from the provider
    try {
      const network = await provider.getNetwork();
      return {
        name: `AVO ${network.chainId === 1337 ? 'Devnet' : 'Network'}`,
        chainId: network.chainId,
        isTestnet: network.chainId === 1337,
        nativeCurrency: 'AVO'
      };
    } catch (providerError) {
      console.warn('Could not connect to AVO network, using fallback:', providerError.message);
      return {
        name: 'AVO Devnet (Offline)',
        chainId: 1337,
        isTestnet: true,
        nativeCurrency: 'AVO'
      };
    }
  } catch (error) {
    console.error('Error getting AVO network:', error);
    return { 
      name: 'AVO Network (Error)', 
      chainId: 1337, 
      isTestnet: true, 
      nativeCurrency: 'AVO' 
    };
  }
};

export default {
  getAvoProvider,
  getAvoBalance,
  sendAvoTransaction,
  estimateAvoGasCost,
  getAvoNetworkConfig,
  getAvoNetworks,
  getAvoTransactionHistory,
  getAvoNetworkStatus,
  getAvoPrice,
  getAvoTokenPrices,
  getAvoRecentTransactions,
  getAvoTokenBalances,
  getCurrentAvoNetwork
};